// ============================================================
// NETWORK SERVER ADAPTER (Phase 3c, extended in 3d)
// ============================================================
//
// Implements the (now async) ServerAdapter interface by talking to
// the authoritative game server over WebSocket.
//
// Step 5 scope — TRULY SERVER-AUTHORITATIVE:
//   - The internal local StateManager is gone. Every adapter method
//     issues an RPC and awaits the server's reply.
//   - Outgoing RPCs are serialised as:
//       { type: "rpc", id, method, params }
//     and each pending call resolves / rejects when the matching
//     { type: "rpcResult", id, ok, result|error } comes back.
//   - Outgoing RPCs sent before the socket is open are queued and
//     flushed on 'open'.
//
// Phase 3d Step 3:
//   - Implements createRoom / joinRoom RPCs.
//   - Server-pushed events (`{ type: "event", event: <name>, ... }`)
//     are dispatched to subscribers registered via onEvent.
//
// Intentional limitations:
//   - No reconnect / retry logic (Phase 4).
//   - If the socket closes with in-flight RPCs, they reject with
//     a 'socket closed' error. Game.ts surfaces this as a game-
//     over state; manual restart is expected.

import { FALLBACK_NET_URL } from './netUrl.ts';
import type { BattleUnit, Base } from '../BattleUnit.ts';
import type { LeaderId } from '../constants.ts';
import type {
  BattleEvent,
  SpellCommand,
  SwapRequest,
  TickCommands,
  ValidationResult,
} from '../server/types.ts';
import type { ServerAdapter } from './ServerAdapter.ts';

export type NetStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

interface PendingRpc {
  method: string;
  resolve: (result: unknown) => void;
  reject: (err: Error) => void;
}

export class NetworkServerAdapter implements ServerAdapter {
  readonly url: string;
  status: NetStatus = 'idle';
  lastError: string | null = null;
  sessionId: string | null = null;

  /** Most recent server response per method — handy for devtools inspection. */
  lastResults: Record<string, unknown> = {};
  /** Most recent server error per method. */
  lastErrors: Record<string, string> = {};

  private ws: WebSocket | null = null;
  private outQueue: string[] = [];
  private nextRpcId = 1;
  private pending = new Map<number, PendingRpc>();
  private eventHandlers = new Map<string, Set<(data: any) => void>>();
  /**
   * True if `disconnect()` initiated the upcoming socket close, so the
   * onclose handler should NOT fire the synthetic 'connectionLost'
   * event. Distinguishes user-initiated teardown (restart / surrender)
   * from involuntary drops (network blip / server kill).
   */
  private _deliberatelyClosing = false;

  constructor(url: string = FALLBACK_NET_URL) {
    this.url = url;
    this.openSocket();
    // Expose for devtools inspection.
    (window as any).__netAdapter = this;
  }

  // -----------------------------------------------------------
  // Socket plumbing
  // -----------------------------------------------------------

  private openSocket(): void {
    this.status = 'connecting';
    this.lastError = null;
    try {
      this.ws = new WebSocket(this.url);
    } catch (err) {
      this.status = 'error';
      this.lastError = (err as Error).message;
      console.error('[net] failed to construct WebSocket:', this.lastError);
      return;
    }

    this.ws.onopen = () => {
      this.status = 'open';
      console.log('[net] connected to ' + this.url);
      for (const msg of this.outQueue) this.ws!.send(msg);
      this.outQueue.length = 0;
    };

    this.ws.onmessage = (ev) => {
      let parsed: any = null;
      try { parsed = JSON.parse(String(ev.data)); } catch { /* ignore */ }

      if (parsed && parsed.type === 'hello' && parsed.sessionId) {
        this.sessionId = parsed.sessionId;
        console.log('[net] server hello — sessionId=' + this.sessionId);
        return;
      }

      if (parsed && parsed.type === 'rpcResult' && typeof parsed.id === 'number') {
        const p = this.pending.get(parsed.id);
        if (!p) {
          console.warn('[net] rpcResult for unknown id=' + parsed.id);
          return;
        }
        this.pending.delete(parsed.id);
        if (parsed.ok) {
          this.lastResults[p.method] = parsed.result;
          if (p.method === 'executeExec' && parsed.result) {
            const r = parsed.result as { events?: unknown[]; winner?: number };
            console.log(
              '[net] <- #' + parsed.id + ' ' + p.method +
              ' events=' + (r.events?.length ?? '?') +
              ' winner=' + r.winner,
            );
          } else {
            console.log('[net] <- #' + parsed.id + ' ' + p.method, parsed.result);
          }
          p.resolve(parsed.result);
        } else {
          const reason = String(parsed.error ?? 'unknown server error');
          this.lastErrors[p.method] = reason;
          console.warn('[net] <- #' + parsed.id + ' ' + p.method + ' ERR:', reason);
          p.reject(new Error(reason));
        }
        return;
      }

      // Phase 3d Step 3: server push events (no rpcId).
      if (parsed && parsed.type === 'event' && typeof parsed.event === 'string') {
        const name = parsed.event;
        console.log('[net] <- event:' + name, parsed);
        const handlers = this.eventHandlers.get(name);
        if (handlers) {
          for (const h of handlers) {
            try { h(parsed); } catch (err) {
              console.error('[net] event handler for ' + name + ' threw:', err);
            }
          }
        }
        return;
      }

      console.log('[net] <-', ev.data);
    };

    this.ws.onerror = () => {
      // Browsers don't surface detailed reasons here for security.
      this.status = 'error';
      this.lastError = 'socket error (see browser console)';
      console.error('[net] socket error');
    };

    this.ws.onclose = (ev) => {
      this.status = 'closed';
      console.log('[net] closed (code=' + ev.code + (ev.reason ? ', reason=' + ev.reason : '') + ')');
      // Snapshot + reset the deliberate-close flag BEFORE rejecting RPCs,
      // since handlers may trigger more teardown.
      const wasDeliberate = this._deliberatelyClosing;
      this._deliberatelyClosing = false;
      // Fail any in-flight RPCs so awaiters don't hang forever.
      const pending = Array.from(this.pending.values());
      this.pending.clear();
      for (const p of pending) {
        p.reject(new Error('socket closed before server responded'));
      }
      // Synthetic 'connectionLost' push — fires only when the close was
      // NOT user-initiated (i.e. network drop, server kill, browser
      // backgrounding the tab on mobile, …). Game.ts subscribes to this
      // to award the local player a defeat + bounce back to MENU rather
      // than sit frozen behind the "Waiting for opponent" overlay.
      if (!wasDeliberate) {
        const handlers = this.eventHandlers.get('connectionLost');
        if (handlers) {
          for (const h of handlers) {
            try { h({ type: 'event', event: 'connectionLost', code: ev.code, reason: ev.reason }); }
            catch (err) {
              console.error('[net] connectionLost handler threw:', err);
            }
          }
        }
      }
    };
  }

  /** Send an RPC and resolve/reject when the matching result arrives. */
  private callRpc<T = unknown>(method: string, params: unknown): Promise<T> {
    const id = this.nextRpcId++;
    const payload = JSON.stringify({ type: 'rpc', id, method, params });
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, {
        method,
        resolve: resolve as (result: unknown) => void,
        reject,
      });
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(payload);
      } else if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
        this.outQueue.push(payload);
      } else {
        // CLOSING / CLOSED — reject immediately.
        this.pending.delete(id);
        reject(new Error('socket not open (status=' + this.status + ')'));
      }
    });
  }

  /** For tests / teardown. */
  disconnect(): void {
    // Mark the upcoming close as user-initiated so onclose suppresses
    // the synthetic 'connectionLost' event. Without this, restart() /
    // confirmSurrender() / etc. would re-enter the disconnect handler
    // and overwrite the surrender/MENU result.
    this._deliberatelyClosing = true;
    if (this.ws) {
      try { this.ws.close(1000, 'client disconnect'); } catch { /* ignore */ }
      this.ws = null;
    }
    this.status = 'closed';
    // onclose will reject any in-flight RPCs; if onclose doesn't fire
    // (e.g. ws already null), clear them ourselves.
    const pending = Array.from(this.pending.values());
    this.pending.clear();
    for (const p of pending) {
      p.reject(new Error('adapter disconnected'));
    }
  }

  // -----------------------------------------------------------
  // ServerAdapter — Phase 3d Room lifecycle
  // -----------------------------------------------------------

  async createRoom(): Promise<{ roomCode: string; myTeam: 0; terrainSeed: number }> {
    return this.callRpc<{ roomCode: string; myTeam: 0; terrainSeed: number }>('createRoom', {});
  }

  async joinRoom(code: string): Promise<{
    roomCode: string;
    myTeam: 1;
    terrainSeed: number;
    deployDeadline?: number;
  }> {
    return this.callRpc<{
      roomCode: string;
      myTeam: 1;
      terrainSeed: number;
      deployDeadline?: number;
    }>('joinRoom', { code });
  }

  onEvent(name: string, handler: (data: any) => void): () => void {
    let set = this.eventHandlers.get(name);
    if (!set) { set = new Set(); this.eventHandlers.set(name, set); }
    set.add(handler);
    return () => { set!.delete(handler); };
  }

  // -----------------------------------------------------------
  // ServerAdapter — battle session (existing)
  // -----------------------------------------------------------

  async initBattle(
    units: BattleUnit[],
    bases: Base[],
    seed: number,
    boardSeed: number,
    terrainSeed: number,
    leaderByTeam?: [LeaderId, LeaderId],
  ): Promise<void> {
    // terrainSeed: in PvP the server already chose a seed at createRoom,
    // so this value is logged-but-ignored on the server side. Sending it
    // anyway keeps the wire shape the same as single mode.
    //
    // Session 9 — leaderByTeam is sent so the server's StateManager can
    // store the per-team leader and gate validateSpellCommand /
    // Match3Mirror with the right gemElements pool.
    await this.callRpc<{ ok: true }>('initBattle', {
      units, bases, seed, boardSeed, terrainSeed,
      ...(leaderByTeam ? { leaderByTeam } : {}),
    });
  }

  /** @deprecated See `ServerAdapter.requestSwap` — kept for back-compat. */
  async requestSwap(req: SwapRequest): Promise<ValidationResult> {
    return this.callRpc<ValidationResult>('requestSwap', { req });
  }

  async commitMatchResults(
    team: 0 | 1,
    stats: Record<string | number, number>,
    options?: { swapLog?: SwapRequest[]; round?: number },
  ): Promise<void> {
    // Phase 3e: optional swapLog enables server-side replay validation
    // (shadow mode). Server's reply may include a `replay` outcome string;
    // we ignore it here in shadow mode (Step 4b will surface mismatches
    // via game-end overlay).
    await this.callRpc<{ ok: true; replay?: string }>('commitMatchResults', {
      team,
      stats,
      ...(options?.swapLog !== undefined ? { swapLog: options.swapLog } : {}),
      ...(options?.round !== undefined ? { round: options.round } : {}),
    });
  }

  async submitSchedule(
    team: 0 | 1,
    schedule: SpellCommand[],
  ): Promise<ValidationResult> {
    return this.callRpc<ValidationResult>('submitSchedule', { team, schedule });
  }

  async executeExec(
    cmds: TickCommands,
  ): Promise<{ events: BattleEvent[]; winner: number }> {
    return this.callRpc<{ events: BattleEvent[]; winner: number }>(
      'executeExec',
      { cmds },
    );
  }
}
